// @givmo/app-admin — Settings → "Security" tab (AS-WEB4).
//
// Where a company Owner sets the authentication bar for its linked employees, and reads how the
// roster actually stands against it.
//
// ══ THE HONEST-CLAIMS RAIL — THE REASON THIS FILE IS SHAPED THE WAY IT IS ═════════════════════
// ⚠ Enforcement is clamped server-side by `EMPLOYER_AUTH_POLICY_MAX_TIER` (default `encourage`) and
// that ceiling is NOT served on the admin wire. So this screen can know what the employer STORED and
// it can never know, from the policy alone, whether anything is being enforced. Two rules follow and
// both are load-bearing:
//
//   1. THE EDITOR RENDERS A SETTING, NEVER AN OUTCOME. It says what the company asks for. It does
//      not say employees are now blocked, when they will be, or that a save changed anyone's
//      experience — none of which it is in a position to know.
//   2. THE PANEL REPORTS ONLY WHAT THE SERVER RETURNED. Per-employee `mfa_status` is computed from
//      the EFFECTIVE tier, so it is the one honest witness of whether enforcement is live: any
//      `enrolled`/`pending`/`overdue` proves it is, an all-`not_required` roster proves it is not.
//      Below `require` there is no enrollment signal on the wire at all, so no percentage is shown —
//      see `security-decode.ts`, which holds that rule for both this screen and the roster column.
//
// ══ DARK — THE SERVER ANSWERS, THERE IS NO CLIENT FLAG ════════════════════════════════════════
// The tab reveals when the caller is an Owner AND `GET /admin/company/settings/auth-policy` answers
// 200. That route sits behind the same `_require_employer_auth_policy_enabled` dependency as the
// PUT, so asking IS the gate and there is nothing to keep in step when it flips — the same reasoning
// that left the passkey and step-up gates without a switch (`agent_docs/auth.md`).
//
// ⚠ It hides correctly even where the route does not exist yet: a 405 (the path is registered for
// PUT only until the GET ships) reads exactly like the dark 404, because `probeAuthPolicy` keys on a
// 200 arriving rather than on a status code. So this needs no two-sided coordination and no flag
// flip to be safe — it is invisible everywhere until the backend serves it.
//
// The probe is SILENT (no toast, console line or telemetry — a dark failure is the expected answer)
// and runs at most ONCE PER PAGE LOAD, memoised below. Deliberately no `sessionStorage` layer: the
// passkey probe carries one only because its route shares a 20/IP/hour fail-closed budget with real
// sign-ins, which this ordinary authenticated `/admin/company/*` read does not, and a persisted
// negative would hide the tab for its TTL after the flag flips — the opposite of what this is for.
//
// Because the probes settle asynchronously, settling NOTIFIES (`subscribeSecurityProbes`), and each
// host listens: the portal's tab registration hands the notice to its registry
// (`notifySettingsTabGatesChanged()` — load-bearing, not belt-and-braces: `settings-page.tsx` can
// render exactly once, the role served from the app shell's cache and the company read on a 30s
// `staleTime`, so without it a verdict arriving after that first render would never reveal the tab);
// the console's shell and page read `useSecuritySurfaceVerdict`; and these panels re-render on every
// settling themselves, so each half appears the moment its own probe answers.
//
// A 404 on SAVE is still handled as skew rather than failure: the surface can go dark between the
// probe and the click, and an Owner then gets a considered notice instead of an error they cannot
// act on.
//
// ══ WHO SEES IT — EACH HOST'S OWN GATE (AS-WEB8) ═══════════════════════════════════════════════
// The panels gate nobody themselves; each host decides who may reach them, and only then starts the
// probes. The portal's tab registration (`apps/admin`, `settings-security-tab.tsx`) fails CLOSED on a
// null role, matching the giving-card tab rather than the Match Settings tab's fail-OPEN in-body gate:
// it decides whether a privileged tab EXISTS in the strip (which renders before the role read
// resolves), so showing it on "not yet known" would flash a Security tab at a Member. The console's
// page (`apps/company`) gates on its `write_settings` capability, which maps to `company:edit`, the
// string every write here enforces. Either way the save still surfaces a 403 honestly in case a
// Member ever reaches it.
//
// ══ THE COMPANY TWO-FACTOR REQUIREMENT (AS-BE38 (web)) — REVEALED BY ONE KEY ══════════════════
// Under D-AS-16 a `require` company asks each linked employee without a second factor, on their next
// open, to add one or unlink — no grace period, no email. While that requirement is live the
// approved Required text ("Once an employee's grace period has run out, they keep their account and
// everything your company shares with them …") is no longer true, so four things change, and ONLY
// while the reveal probe's body carries AS-BE49's `employees_without_second_factor`:
//
//   • the Required tile's description becomes the approved text in `security-copy.ts`
//     (`TWO_FACTOR_REQUIREMENT_COPY`, D-AS-39 fix 3), with the L4 lock's exemption where the
//     company's sign-in lock is on (`showsLockExemption`);
//   • the grace picker is not drawn at Required — the requirement runs no window (plan AS-BE38 (b));
//     the whole-state PUT still carries the stored `mfa_grace_days`, so its body is unchanged;
//   • saving a move TO Required opens the Owner's heads-up (`two-factor-requirement-dialog.tsx`),
//     whose Confirm runs the same write and whose Cancel writes nothing;
//   • once that save has succeeded, the card links to the list (the roster's two-factor column on
//     the Employees page). Never before the save, where following a link would drop the unsaved
//     choice for a roster that cannot show anyone yet, and never after a failed save.
//
// ⚠ THE KEY IS THE WHOLE SIGNAL, AND ITS ABSENCE MEANS "TODAY'S MFA CARD", BYTE FOR BYTE. The backend
// omits it while the requirement is dark, for a Member, and before AS-BE49, and in every one of those
// the old ladder (or nothing) is what runs — so the approved text is still the true one. There is no
// client flag to keep in step: as with the tab itself, the server answering is the switch. The probe
// is read once per page load, so a flip reaches an Owner with their next fresh document.

import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { companiesGetCompanyOptions } from '@givmo/api-client/query';
import { isGivmoError, sdk, supportReference, type CompanyDetailedOut } from '@givmo/api-client';
import { extractList } from '@givmo/api-client/list';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  RadioTile,
  RadioTileGroup,
  Select,
  Skeleton,
  testIdProp,
  toast,
} from '@givmo/design-system';
import {
  GRACE_DAY_PRESETS,
  MAX_MFA_GRACE_DAYS,
  MIN_MFA_GRACE_DAYS,
  formatEnrolledShare,
  readAuthPolicy,
  readAuthPolicySeed,
  summarizeCompliance,
  type AuthPolicyState,
  type ComplianceSummary,
  type MfaPolicyTier,
} from './security-decode';
import { saveAuthPolicy } from './auth-policy-transport';
import { readEmployeesWithoutSecondFactor } from './company-security-decode';
import { readSsoConfig as decodeSsoConfig } from './sso-decode';
import { SSO_CONFIG_QUERY_KEY, SsoSection } from './sso-section';
import { AuditExportCard } from './audit-export-card';
import { TwoFactorRequirementDialog } from './two-factor-requirement-dialog';
import { useSecurityHost } from './security-host';
import {
  authPolicyVerdict,
  probedAuthPolicy,
  probedSsoConfig,
  ssoConfigVerdict,
  useProbeVerdicts,
} from './security-probes';
import {
  SECURITY_COPY,
  TWO_FACTOR_REQUIREMENT_COPY,
  countedLabel,
  dayCountLabel,
  enrolledBadge,
  notSetUpBadge,
  overdueBadge,
  pendingBadge,
  type TierCopy,
} from './security-copy';
import { WRAPPING_CARD_TITLE } from './card-title';

// The words are `security-copy.ts`'s; re-exported so this module's surface is unchanged.
export { SECURITY_COPY, countedLabel, type TierCopy } from './security-copy';

const PREFIX = 'settings-security';

/** The portal's registered tab id — kebab-case, part of the `settings-tab-<id>` testid contract, never
 *  renamed. The registration itself is the portal's (`apps/admin`). */
export const SECURITY_TAB_ID = 'security';

/** The compliance read's query key. Distinct from the roster page's infinite key on purpose: this is
 *  a one-shot summary sample, not the paged list, and the two must not evict each other. */
export const MFA_COMPLIANCE_QUERY_KEY = ['admin-company-mfa-compliance'] as const;

/**
 * How many employees the compliance panel counts.
 *
 * ⚠ It is a SAMPLE, and the panel says so, because this screen has no company-wide denominator
 * available: the `{data,meta,links}` envelope's `meta.total_items` is stripped by the response
 * middleware before any caller sees it (`agent_docs/api-client.md` § *Any top-level SIBLING of `data`
 * is unreachable from the SDK*). One extra row is requested purely as the presence probe for a
 * further page — the sanctioned technique for exactly this envelope — and is never counted.
 */
export const COMPLIANCE_SAMPLE_SIZE = 100;

/**
 * The tier tiles as this page load should draw them (AS-BE38 (web)).
 *
 * The approved tiles, untouched, unless the company two-factor requirement is live — then the
 * Required tile alone carries the requirement's description (APPROVED, D-AS-39 fix 3), followed by
 * the L4 lock's exemption where the tab shows it. A pure function of its signals, so the
 * forward-compat promise (no key, today's tiles) is checkable without rendering anything.
 */
export function tierTiles(
  requirementLive: boolean,
  lockExemptionShown = false,
): readonly TierCopy[] {
  if (!requirementLive) return SECURITY_COPY.tiers;
  const copy = TWO_FACTOR_REQUIREMENT_COPY;
  const description = lockExemptionShown
    ? `${copy.requiredDescription} ${copy.lockExemption}`
    : copy.requiredDescription;
  return SECURITY_COPY.tiers.map((option) =>
    option.tier === 'require' ? { ...option, description } : option,
  );
}

/**
 * Whether the requirement's words carry the L4 lock's exemption (the cut-1003k review's F1).
 *
 * The lock binds every linked employee who does not administer the company, and D-AS-16 (g) exempts
 * them from the requirement, so where it is on, "employees need two-factor authentication" is not
 * the whole truth. The SSO section's own read is the live answer — its cache, which a tier save
 * rewrites — and before it lands, the reveal probe's body. Where this page cannot read the mode at all (the SSO
 * surface not served, or a mode this build does not know) the sentence stands: it is true at every
 * tier, and a lock outlives `EMPLOYER_SSO_ENABLED` (backend `lock_claims.py`, AS-BE51).
 */
export function showsLockExemption(ssoConfigBody: unknown): boolean {
  if (ssoConfigBody === null || ssoConfigBody === undefined) return true;
  const mode = decodeSsoConfig(ssoConfigBody).mode;
  return mode === null || mode === 'signin_lock';
}

/**
 * The SSO section's config as its cache holds it, observed without creating the entry: a second
 * `useQuery` here would build it ahead of the section and drop the section's `initialData` (the
 * probe's body), costing it a loading state and a second read.
 */
function useSsoConfigCache(): unknown {
  const queryClient = useQueryClient();
  return useSyncExternalStore(
    (onChange) => queryClient.getQueryCache().subscribe(onChange),
    () => queryClient.getQueryData(SSO_CONFIG_QUERY_KEY),
  );
}

// ── The probes (`security-probes.ts`, AS-WEB20) ─────────────────────────────────────────────────
// Re-exported, so this module's surface did not change when they moved.
export {
  authPolicyVerdict,
  ensureSecurityProbes,
  resetAuthPolicyProbe,
  resetSsoConfigProbe,
  securitySurfaceVerdict,
  ssoConfigVerdict,
  subscribeSecurityProbes,
  useSecuritySurfaceVerdict,
  useSettledSecuritySurfaceVerdict,
  type AuthPolicyVerdict,
} from './security-probes';

// ── Compliance read ─────────────────────────────────────────────────────────────────────────────

/** One sample page of the roster, as `unknown` rows — the statuses are hand-decoded per row. */
async function fetchComplianceSample(signal: AbortSignal): Promise<unknown[]> {
  const { data } = await sdk.companiesGetCompanyEmployees({
    query: {
      'page[limit]': COMPLIANCE_SAMPLE_SIZE + 1,
      'page[offset]': 0,
    },
    signal,
    throwOnError: true,
  });
  return extractList<unknown>(data);
}

/** Count the sample, and report truncation from the probe row rather than counting it. */
export function summarizeSample(rows: readonly unknown[]): ComplianceSummary {
  const truncated = rows.length > COMPLIANCE_SAMPLE_SIZE;
  return {
    ...summarizeCompliance(rows.slice(0, COMPLIANCE_SAMPLE_SIZE)),
    truncated,
  };
}

// ── The tab ─────────────────────────────────────────────────────────────────────────────────────

export interface SettingsSecurityTabProps {
  /**
   * The company, from the host's one `GET /admin/company`: its policy fields seed the editor, and its
   * name is what the L4 ceremony asks an Owner to type.
   */
  company: CompanyDetailedOut;
  /**
   * The policy the dark-gate probe served, for tests. Production passes nothing and the tab reads
   * the module's settled probe body — which is guaranteed present, since the tab only renders once
   * the probe answered 200.
   */
  probedPolicy?: unknown;
  /** Same, for the SSO section's own probe (AS-WEB6). */
  probedSsoConfig?: unknown;
}

export function SettingsSecurityTab({
  company,
  probedPolicy: injected,
  probedSsoConfig: injectedSso,
}: SettingsSecurityTabProps) {
  const queryClient = useQueryClient();
  // Where these panels render (`security-host.tsx`): the portal's tab by default, the console's page.
  const host = useSecurityHost();
  // Re-render on every probe settling, so each half appears the moment its own probe answers.
  useProbeVerdicts();

  // ── Which of the two sections this environment actually serves (AS-WEB6) ─────────────────────
  //
  // The tab now hosts two surfaces behind two INDEPENDENT backend flags, so each renders on its own
  // probe rather than on the tab being visible. The two gates are deliberately not symmetrical:
  //
  //   • THE SSO SECTION RENDERS ONLY ON A SETTLED `available`. It is new, and nothing about it may
  //     appear until the server has said it exists.
  //   • THE MFA SECTION HIDES ONLY ON A SETTLED `unavailable`. `unknown` means "not asked yet", not
  //     "off" — and in production the tab cannot render at all until at least one probe has
  //     settled, so this is a sub-second window that exists only when the SSO probe answered first.
  //     Hiding a live editor for that window costs an Owner a real control; showing it costs, at
  //     worst, a save that answers 404 and is already rendered as the honest "not available on this
  //     environment yet" notice below. Fail-closed belongs on the TAB, where the question is whether
  //     a Member sees a privileged surface at all; this split is between two Owner-only editors.
  const mfaAvailable = authPolicyVerdict() !== 'unavailable';
  const ssoAvailable = ssoConfigVerdict() === 'available';
  // The company read wins whenever it carries the fields; the probe's body is the fallback for a
  // company row written before the AS-BE10 migration, which serves `null` for both. See
  // `readAuthPolicySeed` for why that order and not the reverse.
  const probeSeed = injected ?? probedAuthPolicy();
  const saved = useMemo(() => readAuthPolicySeed(company, probeSeed), [company, probeSeed]);

  // AS-BE38 (web) — the company two-factor requirement is live exactly when the reveal probe's body
  // carried a count (see the file header). The NUMBER is not used here: the heads-up reads its own
  // when it opens, since this one is as old as the page.
  const requirementLive = readEmployeesWithoutSecondFactor(probeSeed) !== null;
  // Where the company's sign-in lock is on, the lines saying who is asked carry its exemption
  // (`showsLockExemption`): the SSO section's live config, else the probe's body when it answered.
  const ssoConfigCache = useSsoConfigCache();
  const lockExemptionShown = showsLockExemption(
    ssoConfigCache ?? (ssoAvailable ? (injectedSso ?? probedSsoConfig()) : undefined),
  );
  const tiles = tierTiles(requirementLive, lockExemptionShown);
  const [confirmingRequire, setConfirmingRequire] = useState(false);
  // Set only by the heads-up's own save succeeding; cleared by a new choice or another save.
  const [listAfterSave, setListAfterSave] = useState(false);

  // The DRAFT the Owner is editing, seeded from the stored policy. Re-seeded on the stored value's
  // SIGNATURE rather than the company object's identity: any refetch of `GET /admin/company` mints a
  // new object, and re-seeding on that would silently discard edits not yet saved.
  const savedSignature = `${saved.tier}:${saved.graceDays}`;
  const [draft, setDraft] = useState<AuthPolicyState>(saved);
  useEffect(() => {
    setDraft(saved);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the signature ON PURPOSE (above).
  }, [savedSignature]);

  // Latched when a save answers 404: the backend surface is dark in this environment, and no number
  // of retries turns a flag on. Latching (rather than reading the error each render) keeps the notice
  // on screen after the mutation resets.
  const [darkSurface, setDarkSurface] = useState(false);
  const [forbidden, setForbidden] = useState(false);

  const compliance = useQuery({
    queryKey: MFA_COMPLIANCE_QUERY_KEY,
    queryFn: ({ signal }) => fetchComplianceSample(signal),
    select: summarizeSample,
    // No roster sample in an environment that does not serve the MFA policy — the panel it feeds is
    // not rendered there, and a request nobody reads is still a request.
    enabled: mfaAvailable,
  });

  const saveMutation = useMutation({
    mutationFn: async (next: AuthPolicyState): Promise<unknown> =>
      saveAuthPolicy({ mfa_policy: next.tier, mfa_grace_days: next.graceDays }),
    onSuccess: (result) => {
      // The PUT returns the saved `CompanyDetailedOut`, so it is written straight into the company
      // cache the settings page reads: the stored policy this tab renders and the object every other
      // tab holds stay one value, with no second round trip and no chance of them disagreeing.
      queryClient.setQueryData(companiesGetCompanyOptions().queryKey, result);
      setDraft(readAuthPolicy(result));
      setDarkSurface(false);
      setForbidden(false);
      toast.success(SECURITY_COPY.savedToast);
      // The tier drives every employee's status, so a change invalidates the compliance sample.
      void queryClient.invalidateQueries({ queryKey: MFA_COMPLIANCE_QUERY_KEY });
    },
    onError: (error) => {
      if (isGivmoError(error) && error.status === 404) {
        setDarkSurface(true);
        return;
      }
      if (isGivmoError(error) && error.status === 403) {
        setForbidden(true);
        return;
      }
      toast.error(isGivmoError(error) ? error.message : SECURITY_COPY.saveFailed);
    },
    // The heads-up closes once its save has an answer either way: the outcomes above render in the
    // card behind it (the saved toast, the dark and Owner-only notes), so a dialog left open would
    // stand between the Owner and the result of the act it confirmed.
    onSettled: () => setConfirmingRequire(false),
  });

  const dirty = savedSignature !== `${draft.tier}:${draft.graceDays}`;

  // AS-BE38 (web) — a move TO Required, while the requirement is live, is confirmed first. Only the
  // move: a save that keeps Required asks nobody anything new.
  const confirmsFirst = requirementLive && draft.tier === 'require' && saved.tier !== 'require';
  const save = () => {
    setListAfterSave(false);
    if (confirmsFirst) {
      setConfirmingRequire(true);
      return;
    }
    saveMutation.mutate(draft);
  };

  return (
    <div
      className={host.underTabStrip ? 'flex flex-col gap-16 pt-16' : 'flex flex-col gap-16'}
      {...testIdProp(`${PREFIX}-tab`)}
    >
      {mfaAvailable && (
        <>
          <p className="font-sans text-s14 font-w400 leading-h15 tracking-normal text-text-secondary">
            {SECURITY_COPY.intro}
          </p>

          <Card>
            <CardHeader className={WRAPPING_CARD_TITLE} title={SECURITY_COPY.cardTitle} />
            <CardBody>
              <div className="flex flex-col gap-16">
                <RadioTileGroup
                  aria-label={SECURITY_COPY.tierGroupLabel}
                  value={draft.tier}
                  onValueChange={(next) => {
                    setListAfterSave(false);
                    setDraft((current) => ({ ...current, tier: next as MfaPolicyTier }));
                  }}
                  testId={`${PREFIX}-tier`}
                >
                  {tiles.map((option) => (
                    <RadioTile
                      key={option.tier}
                      value={option.tier}
                      label={option.label}
                      description={option.description}
                      testId={`${PREFIX}-tier-${option.tier}`}
                    />
                  ))}
                </RadioTileGroup>

                {/* The grace window only means anything at `require`, so it is revealed rather than
                shown-disabled: a picker that cannot affect anything invites an Owner to reason about
                a number that is not in play. The stored value is untouched while hidden.
                AS-BE38 (web): and not at all while the company two-factor requirement is live, which
                runs no window — its note ("nothing is withheld until it runs out") would contradict
                the Required text right above it. */}
                {draft.tier === 'require' && !requirementLive && (
                  <div className="flex flex-col gap-8" {...testIdProp(`${PREFIX}-grace`)}>
                    <Select
                      appearance="saas"
                      label={SECURITY_COPY.graceLabel}
                      options={graceOptions(draft.graceDays)}
                      value={String(draft.graceDays)}
                      onValueChange={(next) =>
                        setDraft((current) => ({
                          ...current,
                          graceDays: parseGraceOption(next, current.graceDays),
                        }))
                      }
                      testId={`${PREFIX}-grace-select`}
                    />
                    <p className="font-sans text-s13 font-w400 leading-h15 tracking-normal text-text-secondary">
                      {SECURITY_COPY.graceNote}
                    </p>
                  </div>
                )}

                {darkSurface && (
                  <p
                    className="font-sans text-s13 font-w400 leading-h15 tracking-normal text-text-secondary"
                    {...testIdProp(`${PREFIX}-unavailable`)}
                  >
                    {SECURITY_COPY.dark}
                  </p>
                )}

                {forbidden && (
                  <p
                    className="font-sans text-s13 font-w500 tracking-normal text-danger-700"
                    {...testIdProp(`${PREFIX}-forbidden`)}
                  >
                    {SECURITY_COPY.forbidden}
                  </p>
                )}

                <div>
                  <Button
                    appearance="saas"
                    tone="primary"
                    disabled={!dirty}
                    loading={saveMutation.isPending}
                    onClick={save}
                    testId={`${PREFIX}-save`}
                  >
                    {SECURITY_COPY.save}
                  </Button>
                </div>

                {/* AS-BE38 (web) — the link to the list, with the saved setting: only after the
                    heads-up's save succeeded (see the file header). The hedge stays, because under a
                    clamping ceiling nobody is asked yet and the roster shows no column. Only where
                    this host serves an Employees page for the person (`security-host.tsx`). */}
                {requirementLive && listAfterSave && host.employees !== null && (
                  <p
                    className="font-sans text-s13 font-w400 leading-h15 tracking-normal text-text-secondary"
                    {...testIdProp(`${PREFIX}-require-saved-list`)}
                  >
                    {TWO_FACTOR_REQUIREMENT_COPY.savedListBefore}
                    <Link
                      to={host.employees}
                      className="font-w600 text-brand-600 hover:underline"
                      {...testIdProp(`${PREFIX}-require-saved-list-link`)}
                    >
                      {TWO_FACTOR_REQUIREMENT_COPY.savedListLink}
                    </Link>
                    {TWO_FACTOR_REQUIREMENT_COPY.savedListAfter}
                  </p>
                )}
              </div>
            </CardBody>
          </Card>

          <CompliancePanel
            query={compliance}
            storedTier={saved.tier}
            requirementLive={requirementLive}
          />

          {requirementLive && (
            <TwoFactorRequirementDialog
              open={confirmingRequire}
              onOpenChange={setConfirmingRequire}
              onConfirm={() =>
                saveMutation.mutate(draft, { onSuccess: () => setListAfterSave(true) })
              }
              submitting={saveMutation.isPending}
              lockExemptionShown={lockExemptionShown}
            />
          )}
        </>
      )}

      {/* AS-WEB6 — the company single sign-on section, on its OWN probe (see the gates above). The
          company object is threaded through for its NAME, which is what the L4 flip ceremony asks an
          Owner to type. */}
      {/* The page's three groups — two-factor, company sign-in, the history export — sit one gap
          step apart (D-AS-38 (1)); inside a group the cards keep the 16px rhythm. `not-first`, so a
          group that opens the surface carries no extra space above it. */}
      {ssoAvailable && (
        <div className="not-first:mt-16">
          <SsoSection companyName={company.name} probedConfig={injectedSso ?? probedSsoConfig()} />
        </div>
      )}

      {/* AS-WEB7 / AS-BE20 — the security audit export, at TAB level rather than inside either
          section, because its backend gate is the DISJUNCTION: `EMPLOYER_SSO_ENABLED` OR
          `EMPLOYER_AUTH_POLICY_ENABLED`. The trail spans both employer security ladders, which are
          independently operator-flippable, so nesting it under either section would 404 an
          employer's audit trail in a state the rollout actually produces — the MFA ladder live and
          SSO still dark — while rows were genuinely being written. That disjunction is exactly this
          TAB's own reveal condition, so it needs no third probe: whenever the tab renders, the
          export's route answers. */}
      <div className="not-first:mt-16">
        <AuditExportCard />
      </div>
    </div>
  );
}

/**
 * The grace-window options.
 *
 * The presets are 7/14/30, but a value already stored outside that set (the endpoint accepts 0–365)
 * is added rather than snapped — silently moving an Owner's stored window to the nearest preset the
 * moment they open this screen would change a policy they did not touch.
 */
export function graceOptions(current: number): { value: string; label: string }[] {
  const days = [...new Set([...GRACE_DAY_PRESETS, current])]
    .filter((day) => day >= MIN_MFA_GRACE_DAYS && day <= MAX_MFA_GRACE_DAYS)
    .sort((a, b) => a - b);
  return days.map((day) => ({
    value: String(day),
    label: dayCountLabel(day),
  }));
}

/** Options are minted from numbers above, so an unparseable value means the control was driven with
 *  something this screen never offered — keep the current window rather than inventing one. */
function parseGraceOption(value: string | null, fallback: number): number {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= MIN_MFA_GRACE_DAYS && parsed <= MAX_MFA_GRACE_DAYS
    ? parsed
    : fallback;
}

function CompliancePanel({
  query,
  storedTier,
  requirementLive,
}: {
  query: ReturnType<typeof useQuery<unknown[], Error, ComplianceSummary>>;
  storedTier: MfaPolicyTier;
  /** The company two-factor requirement is live (the reveal probe served its count): AS-WEB20. */
  requirementLive: boolean;
}) {
  return (
    <Card>
      <CardHeader
        className={WRAPPING_CARD_TITLE}
        title={SECURITY_COPY.panelTitle}
        subtitle={SECURITY_COPY.panelSubtitle}
      />
      <CardBody>
        {query.isPending ? (
          <div className="flex flex-col gap-12" {...testIdProp(`${PREFIX}-compliance-loading`)}>
            <Skeleton className="h-12 w-1/3" />
            <Skeleton className="h-12 w-2/3" />
          </div>
        ) : query.isError ? (
          <div
            className="flex flex-col items-start gap-4"
            {...testIdProp(`${PREFIX}-compliance-error`)}
          >
            <p className="font-sans text-s14 font-w500 tracking-givmo text-danger-700">
              {isGivmoError(query.error) ? query.error.message : SECURITY_COPY.panelFailed}
            </p>
            {isGivmoError(query.error) && supportReference(query.error) !== null && (
              <p className="font-sans text-s12 font-w400 tracking-normal text-text-tertiary">
                {supportReference(query.error)}
              </p>
            )}
            <button
              type="button"
              onClick={() => void query.refetch()}
              className="font-sans text-s13 font-w600 tracking-givmo text-brand-600 hover:underline"
              {...testIdProp(`${PREFIX}-compliance-retry`)}
            >
              {SECURITY_COPY.panelRetry}
            </button>
          </div>
        ) : (
          <ComplianceBody
            summary={query.data}
            storedTier={storedTier}
            requirementLive={requirementLive}
          />
        )}
      </CardBody>
    </Card>
  );
}

/**
 * What the sample actually supports saying.
 *
 * The branches are ordered by how much the wire tells us, most-specific first, and none of them
 * infers a fact the server did not send — in particular the "not being asked" branch is entered
 * because every counted status came back `not_required`, not because of what the tier says.
 */
function ComplianceBody({
  summary,
  storedTier,
  requirementLive,
}: {
  summary: ComplianceSummary;
  storedTier: MfaPolicyTier;
  requirementLive: boolean;
}) {
  if (summary.counted === 0) {
    return (
      <p
        className="font-sans text-s14 font-w400 leading-h15 tracking-normal text-text-secondary"
        {...testIdProp(`${PREFIX}-compliance-empty`)}
      >
        {SECURITY_COPY.panelEmpty}
      </p>
    );
  }

  // Nobody's status was disclosed to this caller — the backend returns an empty status map to
  // anyone without `company:edit`. Say that, rather than reporting a roster of zeroes.
  if (summary.undisclosed === summary.counted) {
    return (
      <p
        className="font-sans text-s14 font-w400 leading-h15 tracking-normal text-text-secondary"
        {...testIdProp(`${PREFIX}-compliance-undisclosed`)}
      >
        {SECURITY_COPY.panelUndisclosed}
      </p>
    );
  }

  if (!summary.enforcementObserved) {
    return (
      <div className="flex flex-col gap-8" {...testIdProp(`${PREFIX}-compliance-not-asked`)}>
        {/* D-AS-39, fix 4e: once Required is saved, the nudge to set Required would contradict the
            line below it — the lead sentence stands alone, with the requirement live or dark (the
            fix's header, "only while the requirement is live", governs the badges, 4a–4d).
            Otherwise the approved line, unchanged. */}
        <p className="font-sans text-s14 font-w400 leading-h15 tracking-normal text-text-secondary">
          {storedTier === 'require'
            ? SECURITY_COPY.panelNotAskedRequireSaved
            : SECURITY_COPY.panelNotAsked}
        </p>
        {/* The employer stored `require` and yet nothing is being asked. Both facts are true and the
            reason (the deployment's enforcement ceiling) is not on this wire, so this states the two
            facts and promises no timing. */}
        {storedTier === 'require' && (
          <p
            className="font-sans text-s13 font-w400 leading-h15 tracking-normal text-text-tertiary"
            {...testIdProp(`${PREFIX}-compliance-stored-require`)}
          >
            {SECURITY_COPY.panelStoredRequireNotLive}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-12" {...testIdProp(`${PREFIX}-compliance-counts`)}>
      <div className="flex flex-wrap items-center gap-12">
        <span
          className="font-sans text-s24 font-w600 tracking-givmo text-text-strong tabular-nums"
          {...testIdProp(`${PREFIX}-compliance-share`)}
        >
          {summary.enrolledShare === null ? '' : formatEnrolledShare(summary.enrolledShare)}
        </span>
        <span className="font-sans text-s14 font-w400 tracking-normal text-text-secondary">
          {SECURITY_COPY.shareSuffix}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-8">
        <Badge tone="success" dot testId={`${PREFIX}-compliance-enrolled`}>
          {enrolledBadge(summary.enrolled)}
        </Badge>
        {/* AS-WEB20 (D-AS-39, fix 4): the requirement runs no grace period — nobody is ever
            "within" one, and an employee without a second factor is simply not set up. With the
            requirement dark, the approved badges, unchanged. */}
        {!requirementLive && (
          <Badge tone="neutral" dot testId={`${PREFIX}-compliance-pending`}>
            {pendingBadge(summary.pending)}
          </Badge>
        )}
        <Badge tone="danger" dot testId={`${PREFIX}-compliance-overdue`}>
          {requirementLive ? notSetUpBadge(summary.overdue) : overdueBadge(summary.overdue)}
        </Badge>
      </div>

      <p className="font-sans text-s12 font-w400 tracking-normal text-text-tertiary">
        {countedLabel(summary)}
      </p>
    </div>
  );
}
